import React from 'react';
import { Link } from 'react-router-dom';
import KineticHeadline from '../motion/KineticHeadline';
import Reveal from '../motion/Reveal';
import Magnetic from '../motion/Magnetic';
import CountUp from '../motion/CountUp';
import LoopVideo from '../motion/LoopVideo';
import Claws from '../motion/Claws';
import SectionLight from '../motion/SectionLight';
import DemoPicks from '../DemoPicks';

function OctaRing() {
  // Eight-sided cage outline, two strokes, slowly turning behind the demo.
  const pts = (r) => Array.from({ length: 8 }, (_, i) => {
    const a = (Math.PI / 8) + (i * Math.PI) / 4;
    return `${(50 + r * Math.cos(a)).toFixed(2)},${(50 + r * Math.sin(a)).toFixed(2)}`;
  }).join(' ');
  return (
    <div className="pnp-octa-ring pnp-hero-ring" aria-hidden="true">
      <svg viewBox="0 0 100 100">
        <defs>
          <linearGradient id="pnpRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ff5a4e" stopOpacity="0.9" />
            <stop offset="0.5" stopColor="#ff2e2a" stopOpacity="0.15" />
            <stop offset="1" stopColor="#ffc857" stopOpacity="0.55" />
          </linearGradient>
        </defs>
        <polygon points={pts(46)} fill="none" stroke="url(#pnpRing)" strokeWidth="0.35" />
        <polygon points={pts(41)} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.2" strokeDasharray="0.6 1.2" />
      </svg>
    </div>
  );
}

/**
 * Hero: the first thing you see is the live demo. Kinetic title card on the
 * left (head + body); on the right, the real DemoPicks widget on the next
 * card, lit on a stage in front of the dimmed hero-loop video, the octagon
 * and the claws. Phones: eyebrow + a two-line headline, then the demo, then
 * the lead, CTAs and live stats.
 */
export default function Hero({ stats, titleShotInvite }) {
  return (
    <section className="pnp-hero" aria-labelledby="pnp-hero-title">
      <SectionLight fadeTop={false} bleed="0px">
        <div className="pnp-octagrid" style={{ opacity: 0.9 }} />
        <div className="pnp-glow" style={{ right: '-22%', left: 'auto', top: '-6%', width: 1200, height: 1200 }} />
        <div className="pnp-glow pnp-glow-gold" style={{ left: '-30%', top: '40%', width: 900, height: 900, opacity: 0.35 }} />
      </SectionLight>

      <div className="pnp-wrap pnp-hero-grid">
        <div className="pnp-hero-head">
          {titleShotInvite ? (
            <Reveal rise={false}>
              <div className="pnp-invite" role="status">
                <img src="/images/title-belt.svg" alt="" />
                You&apos;ve been challenged to the PNP Title Shot. Sign up to enter free.
              </div>
            </Reveal>
          ) : null}
          <h1 className="pnp-hero-title" id="pnp-hero-title">
            <Reveal rise={false} as="span" style={{ display: 'block' }}>
              <span className="pnp-eyebrow">
                <span className="pnp-eyebrow-claw" aria-hidden="true"><i /><i /><i /></span>
                <span>Free UFC pick&apos;em pools</span>
              </span>
            </Reveal>
            <KineticHeadline as="span" className="pnp-display pnp-h1" text={'Pick the card.\nPray for the *finish.*'} delay={120} />
          </h1>
        </div>

        <div className="pnp-hero-stage" id="demo">
          <div className="pnp-hero-visual" aria-hidden="true">
            <OctaRing />
            <LoopVideo
              name="hero-loop"
              feather="soft"
              eager
              className="pnp-hero-video"
              label=""
            />
            <Reveal rise={false} style={{ position: 'absolute', inset: 0 }}>
              <Claws className="pnp-hero-claws" />
              <Claws className="pnp-hero-claws-b" thin />
            </Reveal>
          </div>
          <Reveal delay={160} className="pnp-hero-demo">
            <DemoPicks />
          </Reveal>
          <Reveal delay={420} className="pnp-hero-demo-note">
            <p><span className="pnp-live-dot" aria-hidden="true" /> No account needed. Real fights from the next UFC card.</p>
          </Reveal>
        </div>

        <div className="pnp-hero-body">
          <Reveal delay={480}>
            <p className="pnp-lead pnp-hero-lead">
              Call the winner and the method for every fight on the card. Chase the Title Shot belt in the free public
              pool, or start a private pool and talk trash with your crew in Corner Chat.
            </p>
          </Reveal>
          <Reveal delay={600} className="pnp-hero-ctas">
            <Magnetic>
              <Link to="/join/TITLESHOT" className="pnp-btn pnp-btn-primary pnp-btn-lg">
                Join the Title Shot <span className="pnp-btn-arrow" aria-hidden="true">→</span>
              </Link>
            </Magnetic>
            <Magnetic strength={0.2}>
              <Link to="/register?redirect=/create-pool" className="pnp-btn pnp-btn-ghost pnp-btn-lg">Start a private pool</Link>
            </Magnetic>
          </Reveal>
          <Reveal delay={680}>
            <p className="pnp-hero-signin">Already in a pool? <Link to="/login">Sign in</Link></p>
          </Reveal>
          <Reveal delay={760} className="pnp-stats">
            <div className="pnp-stat">
              <CountUp to={stats ? stats.public_pool_members : null} className="pnp-stat-num pnp-gold" />
              <span className="pnp-stat-label">In for the belt</span>
            </div>
            <span className="pnp-stat-sep" aria-hidden="true" />
            <div className="pnp-stat">
              <CountUp to={stats ? stats.private_pool_count : null} className="pnp-stat-num" />
              <span className="pnp-stat-label">Private pools running</span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
